import { useState, useEffect } from 'react'
import { X, User, Mail, Shield, Calendar, Clock, Activity } from 'lucide-react'
import api from '../services/api'

const UserDetailsModal = ({ isOpen, onClose, user }) => {
    const [userDetails, setUserDetails] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (isOpen && user) {
            fetchUserDetails()
        }
    }, [isOpen, user])

    const fetchUserDetails = async () => {
        if (!user?._id) return

        setLoading(true)
        try {
            const response = await api.get(`/users/${user._id}`)
            setUserDetails(response.data.data.user)
        } catch (error) {
            console.error('Error fetching user details:', error)
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Never'
        return new Date(dateString).toLocaleString()
    }

    const getStatusColor = (isActive) => {
        return isActive
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <User className="h-5 w-5 text-indigo-600" />
                        <h2 className="text-lg font-semibold text-gray-900">
                            User Details
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
                        </div>
                    ) : userDetails ? (
                        <div className="space-y-6">
                            {/* User Avatar and Basic Info */}
                            <div className="flex items-center space-x-4">
                                <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center">
                                    <span className="text-2xl font-bold text-indigo-700">
                                        {userDetails.username?.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        {userDetails.username}
                                    </h3>
                                    <p className="text-gray-600">{userDetails.email}</p>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mt-2 ${getStatusColor(userDetails.isActive)}`}>
                                        {userDetails.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>

                            {/* User Information Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Basic Information */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-medium text-gray-900 flex items-center">
                                        <User className="h-4 w-4 mr-2" />
                                        Basic Information
                                    </h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500">Username</label>
                                            <p className="text-sm text-gray-900">{userDetails.username}</p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500">Email</label>
                                            <p className="text-sm text-gray-900 flex items-center">
                                                <Mail className="h-3 w-3 mr-1" />
                                                {userDetails.email}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500">Role</label>
                                            <p className="text-sm text-gray-900 flex items-center">
                                                <Shield className="h-3 w-3 mr-1" />
                                                {userDetails.role?.name || 'No role assigned'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Account Information */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-medium text-gray-900 flex items-center">
                                        <Activity className="h-4 w-4 mr-2" />
                                        Account Information
                                    </h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500">Account Status</label>
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(userDetails.isActive)}`}>
                                                {userDetails.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500">Last Login</label>
                                            <p className="text-sm text-gray-900 flex items-center">
                                                <Clock className="h-3 w-3 mr-1" />
                                                {formatDate(userDetails.lastLogin)}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500">Member Since</label>
                                            <p className="text-sm text-gray-900 flex items-center">
                                                <Calendar className="h-3 w-3 mr-1" />
                                                {formatDate(userDetails.createdAt)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Role Permissions */}
                            {userDetails.role && (
                                <div className="space-y-4">
                                    <h4 className="text-sm font-medium text-gray-900 flex items-center">
                                        <Shield className="h-4 w-4 mr-2" />
                                        Role Permissions
                                    </h4>
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {Object.entries(userDetails.role.permissions || {}).map(([resource, permissions]) => (
                                                <div key={resource} className="space-y-2">
                                                    <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                                        {resource}
                                                    </h5>
                                                    <div className="space-y-1">
                                                        {Object.entries(permissions).map(([action, allowed]) => (
                                                            <div key={action} className="flex items-center justify-between">
                                                                <span className="text-xs text-gray-600 capitalize">
                                                                    {action}
                                                                </span>
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${allowed
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {allowed ? 'Allowed' : 'Denied'}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Account Statistics */}
                            <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-900 flex items-center">
                                    <Activity className="h-4 w-4 mr-2" />
                                    Account Statistics
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-blue-600">
                                            {userDetails.loginAttempts || 0}
                                        </div>
                                        <div className="text-xs text-blue-600">Login Attempts</div>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-green-600">
                                            {userDetails.isLocked ? 'Yes' : 'No'}
                                        </div>
                                        <div className="text-xs text-green-600">Account Locked</div>
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-purple-600">
                                            {userDetails.role?.level || 0}
                                        </div>
                                        <div className="text-xs text-purple-600">Role Level</div>
                                    </div>
                                    <div className="bg-orange-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-orange-600">
                                            {userDetails.isActive ? 'Yes' : 'No'}
                                        </div>
                                        <div className="text-xs text-orange-600">Active Status</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <p className="text-gray-500">No user details available</p>
                        </div>
                    )}
                </div>

                {/* Modal Actions */}
                <div className="flex justify-end p-6 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="btn btn-outline"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

export default UserDetailsModal 